import { useMemo } from 'react';
import { useStore } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { StepDraft } from '@shared/flow/enrollment';

/**
 * The Gong step-override draft store — one rep's unsaved per-step
 * Subject+Body edits (and the send-time save failures for them), keyed
 * `${itemId}:${stepNumber}`.
 *
 * ── Why a store at all (typing-lag fix, 2026-08-20) ──────────────────────
 * These drafts used to be `EnrollQueueProvider` React state, so
 * `editStepOverride`/`resetStepOverrideToDefault` changed identity on every
 * keystroke; both are members of the provider's giant context `value`, so
 * EVERY consumer of `useEnrollQueue()` (the whole item list, the sidebar,
 * every other item's editor) re-rendered on every keystroke into ANY
 * step-override field anywhere in the queue. Moving the drafts OUT of React
 * state fixes that at the root: the context's accessors
 * (`stepOverrideDraftFor`/`stepSaveErrorFor`/`subscribeStepOverrides`) read
 * this store directly and are therefore referentially STABLE, and the one
 * component that must re-render on an edit — `StepOverrideRow`
 * (StepOverrideEditor.tsx) — subscribes to its own key, bypassing the
 * context/props chain for that update.
 *
 * ── Why zustand (GTMOPS-2285 / `terra/no-hand-rolled-external-store`) ────
 * The first version of this store was hand-rolled: mutable Maps plus a
 * per-key listener-`Set` registry plus `useSyncExternalStore` call sites.
 * That is exactly the shape root CLAUDE.md ("Zustand for Module-Scope
 * Client State") bans, so the plumbing is now zustand's:
 * `subscribeStepOverrideKey` filters zustand's own `(state, previous)`
 * notification down to one key, and readers use zustand's `useStore` React
 * binding (`useStepOverrideEntry` below) instead of `useSyncExternalStore`.
 * Same observable behavior, none of the hand-rolled plumbing.
 *
 * ── Why PER MOUNT, not a module singleton ────────────────────────────────
 * `createStepOverrideStore()` is a FACTORY and `EnrollQueueProvider` holds
 * one instance per mount (`useState(() => createStepOverrideStore())`).
 * Unlike `impersonate-write-mode` (genuinely one cross-app flag), this state
 * is queue-session-scoped, and a module-level singleton would leak drafts
 * between independently-rendered `EnrollQueueProvider`s — most visibly in
 * tests, where two providers in one file would see each other's edits. Do
 * not "simplify" this into a module-level `create()` store; the isolation is
 * the point (regression-tested in `__tests__/step-override-drafts.test.ts`
 * and `__tests__/EnrollQueue.test.tsx`).
 *
 * `createStore` from `zustand/vanilla`, not `create` from `zustand`: the
 * instance is passed around explicitly (provider → guard, props → rows), so
 * it needs no bound React hook of its own.
 *
 * ── Map discipline ──────────────────────────────────────────────────────
 * Both Maps are COPY-ON-WRITE (`new Map(prev)` then `setState`), which is
 * what lets `subscribeStepOverrideKey` compare per-key values across a
 * notification — and why the state type exposes them as `ReadonlyMap`.
 * Never mutate a stored Map in place.
 */

/**
 * The full effective Subject+Body snapshot one row commits on every edit
 * (plan 2026-08-19-004, Decision #3: full-snapshot capture, never partial
 * patches). Never carries `null`: the editor only ever commits displayed
 * text; "use flow default" travels through a `values: null` DRAFT (below),
 * not a null-field snapshot.
 *
 * Declared here rather than in `StepOverrideEditor.tsx` (which re-exports it
 * for its portable prop contract) so the store module has no import edge
 * back into the component — `import/no-cycle`.
 */
export interface StepOverrideSnapshot {
  subject_draft: string;
  body_draft: string;
}

/**
 * One unsaved step-override edit. `stepType` rides along because the PUT
 * route requires it (`StepDraftWriteSchema`).
 */
export interface StepOverrideDraft {
  itemId: number;
  stepNumber: number;
  stepType: string;
  /** Full effective snapshot as displayed. `null` = "use flow default" intent — Send issues the DELETE (Decision #4). */
  values: StepOverrideSnapshot | null;
}

export interface StepOverrideDraftsState {
  /** Unsaved edits, keyed `${itemId}:${stepNumber}`. Copy-on-write. */
  drafts: ReadonlyMap<string, StepOverrideDraft>;
  /** The last `saveStepOverrides` failure message per key. Cleared on edit/reset/success. Copy-on-write. */
  saveErrors: ReadonlyMap<string, string>;
  /**
   * U2 (gong-bulk-step-defaults-bugfix plan, BLOCK-SHIP) — a successful
   * PUT/DELETE's draft, RETAINED here instead of deleted outright, until a
   * queue read reflects it (`stepDraftReflectsAckedDraft` below). Mirrors
   * the base-copy autosave rail's own `ackedPatch` fix for the identical
   * post-ack/pre-refetch window (`EnrollQueueContext.tsx`'s
   * `DebouncedWriteEntry.ackedPatch` doc comment) — `saveStepOverrides`'
   * QUEUE_KEY invalidation is fire-and-forget, so between the ack and the
   * refetch landing there is a real window where a naive delete leaves a row
   * with neither ownership signal, letting `resolveEffectiveStepValues` fall
   * through to the batch rung and `deriveStagedStepWrites` re-plan a batch
   * write over the just-saved override.
   *
   * `stepOverrideDraftFor` (`EnrollQueueContext.tsx`) reads
   * `drafts.get(key) ?? acked.get(key) ?? null` — one change that keeps both
   * the display rung and the materialization guard honest through the
   * window. Released by the queue-driven sweep effect, never by this
   * module. Copy-on-write, like `drafts`/`saveErrors`.
   */
  acked: ReadonlyMap<string, StepOverrideDraft>;
}

/**
 * Exported per the CLAUDE.md zustand convention. Both Maps are shared as the
 * seed of every instance, which is safe only because writes always copy —
 * see "Map discipline" above.
 */
export const initialState: StepOverrideDraftsState = {
  drafts: new Map(),
  saveErrors: new Map(),
  acked: new Map(),
};

/** A store instance. Narrow by construction — vanilla `StoreApi`, no bound hook. */
export type StepOverrideStore = StoreApi<StepOverrideDraftsState>;

/**
 * The read-only half, for props: a consumer that only subscribes and reads
 * (the unsaved-changes guard) takes this, so a component can never write
 * drafts behind the context's back.
 */
export type ReadonlyStepOverrideStore = Pick<StepOverrideStore, 'getState' | 'getInitialState' | 'subscribe'>;

/** The one key shape: `${itemId}:${stepNumber}`. */
export function stepOverrideKey(itemId: number, stepNumber: number): string {
  return `${itemId}:${stepNumber}`;
}

/**
 * Key prefix for every entry belonging to one item. The colon is part of the
 * prefix on purpose — `'1'` would also match item 11's keys.
 */
export function stepOverrideItemPrefix(itemId: number): string {
  return `${itemId}:`;
}

/** One instance per `EnrollQueueProvider` mount — see the header. */
export function createStepOverrideStore(): StepOverrideStore {
  return createStore<StepOverrideDraftsState>(() => ({ ...initialState }));
}

/** "Is anything unsaved?" — the beforeunload guard's selector. */
export function hasAnyStepOverrideDraft(state: StepOverrideDraftsState): boolean {
  return state.drafts.size > 0;
}

/**
 * The entries swept out of one item by `clearStepOverrideDraftsForItem`,
 * returned so a caller can restore them if the operation that motivated the
 * sweep (a reassign PATCH) ends up failing. Empty maps on the no-op branch —
 * a caller can always iterate the result without a null check.
 */
export interface StepOverrideItemSweep {
  drafts: ReadonlyMap<string, StepOverrideDraft>;
  saveErrors: ReadonlyMap<string, string>;
}

/**
 * U7b (reassign-drafts fix, round-6 adversarial follow-up on Codex #3) —
 * drops every LOCAL draft/save-error entry belonging to one item, with NO
 * server write. Called when an item moves to a different play: the
 * server-side reset (`clearStepDraftsForItem`, `queue.ts`'s reassignment
 * block, U7a) already deletes the item's PERSISTED step drafts in the SAME
 * PATCH, but nothing about that PATCH touches this client-only store — so
 * without this, a stale local draft typed under the OLD play would either
 * display under the new play's identically-numbered step, or (worse) get
 * pushed to the server on the item's NEXT Send, since `saveStepOverrides`
 * enumerates entries by item-id prefix only, with no per-play scoping.
 *
 * Deliberately NOT `resetStepOverrideToDefault`-for-every-key: that records a
 * DELETE *intent* to be issued at the next Send, which would fire a DELETE
 * against the NEW play's (possibly identically-numbered) step the moment the
 * rep sends — clobbering a fresh override they made under the new play. This
 * removes the Map entries outright instead, exactly as if the edit had never
 * happened.
 *
 * A no-op `setState` (same identity, no re-render) when the item has nothing
 * tracked — the common case for a reassign on an item nobody has edited yet.
 *
 * Reflection fix (2026-09-01) — returns the swept entries (previously
 * `void`) so a caller whose reassign PATCH then fails can restore them via
 * `restoreStepOverrideDraftsForItem` below; the item never actually moved
 * server-side, so the rep's unsaved edits should come back exactly as typed.
 */
export function clearStepOverrideDraftsForItem(store: StepOverrideStore, itemId: number): StepOverrideItemSweep {
  const prefix = stepOverrideItemPrefix(itemId);
  const { drafts, saveErrors, acked } = store.getState();
  const hasDraft = Array.from(drafts.keys()).some(key => key.startsWith(prefix));
  const hasError = Array.from(saveErrors.keys()).some(key => key.startsWith(prefix));
  // U2 — an acked entry alone (no unsaved draft/error left) still needs
  // sweeping on reassign: the OLD play's acked entries must not display
  // under the new play's identically-numbered steps.
  const hasAcked = Array.from(acked.keys()).some(key => key.startsWith(prefix));
  if (!hasDraft && !hasError && !hasAcked) return { drafts: new Map(), saveErrors: new Map() };
  const sweptDrafts = new Map<string, StepOverrideDraft>();
  const nextDrafts = new Map(drafts);
  for (const key of drafts.keys()) {
    if (!key.startsWith(prefix)) continue;
    const entry = nextDrafts.get(key);
    if (entry) sweptDrafts.set(key, entry);
    nextDrafts.delete(key);
  }
  const sweptSaveErrors = new Map<string, string>();
  const nextSaveErrors = new Map(saveErrors);
  for (const key of saveErrors.keys()) {
    if (!key.startsWith(prefix)) continue;
    const message = nextSaveErrors.get(key);
    if (message !== undefined) sweptSaveErrors.set(key, message);
    nextSaveErrors.delete(key);
  }
  // U2 — swept, but deliberately NOT returned/restorable (see
  // `restoreStepOverrideDraftsForItem`'s doc comment below): an acked entry
  // is already saved server-side, so there is nothing a failed reassign
  // needs to bring back for it.
  let nextAcked: Map<string, StepOverrideDraft> | null = null;
  if (hasAcked) {
    nextAcked = new Map(acked);
    for (const key of acked.keys()) {
      if (key.startsWith(prefix)) nextAcked.delete(key);
    }
  }
  store.setState({ drafts: nextDrafts, saveErrors: nextSaveErrors, ...(nextAcked ? { acked: nextAcked } : {}) });
  return { drafts: sweptDrafts, saveErrors: sweptSaveErrors };
}

/**
 * The undo half of the reassign-failure fix (Reflection, 2026-09-01):
 * re-adds a previously swept item's entries — but SKIPPING any key that
 * currently has an entry, so a draft the rep typed AFTER the sweep (editor
 * reopened while the reassign PATCH was still in flight) wins over the
 * restored one. Same keystrokes-win posture as `useDraftState.reseedIfUnchanged`.
 *
 * A no-op `setState` (same identity, no re-render) when the sweep is empty —
 * mirrors `clearStepOverrideDraftsForItem`'s own no-op branch — and also when
 * every swept key has since been re-populated (nothing left to restore).
 *
 * U2 — deliberately does NOT restore `acked` entries `clearStepOverrideDraftsForItem`
 * swept (that function drops them from the store but never returns them in
 * the `sweep` it hands back here). On a failed reassign, the PATCH never
 * committed, so the item's persisted step-draft rows were never touched —
 * the persisted rung (`item.step_drafts`) already shows the exact same
 * values the swept `acked` entry did, and re-adding it would just be
 * retaining a write that already has nothing left to protect against.
 */
export function restoreStepOverrideDraftsForItem(
  store: StepOverrideStore,
  itemId: number,
  sweep: StepOverrideItemSweep,
): void {
  if (sweep.drafts.size === 0 && sweep.saveErrors.size === 0) return;
  const { drafts, saveErrors } = store.getState();

  // Mutable copies built lazily (`null` until the first actual restore), so
  // the "sweep is entirely stale, nothing to restore" case below stays a
  // same-identity no-op — explicit `Map | null` typing (rather than seeding
  // from the `ReadonlyMap` state fields directly) is what keeps `.set()`
  // legal here.
  let nextDrafts: Map<string, StepOverrideDraft> | null = null;
  for (const [key, entry] of sweep.drafts) {
    if (drafts.has(key)) continue;
    if (!nextDrafts) nextDrafts = new Map(drafts);
    nextDrafts.set(key, entry);
  }

  let nextSaveErrors: Map<string, string> | null = null;
  for (const [key, message] of sweep.saveErrors) {
    if (saveErrors.has(key)) continue;
    if (!nextSaveErrors) nextSaveErrors = new Map(saveErrors);
    nextSaveErrors.set(key, message);
  }

  if (!nextDrafts && !nextSaveErrors) return;
  store.setState({ drafts: nextDrafts ?? drafts, saveErrors: nextSaveErrors ?? saveErrors });
}

/**
 * Subscribe to ONE key's entry — the draft AND its save error, which share
 * the key (a row reads both off one subscription).
 *
 * zustand notifies every subscriber on every `setState`; the per-key
 * comparison here is what preserves the property the hand-rolled per-key
 * listener registry provided: editing step 2 must not notify (and so must
 * not re-render) step 1's row, another item's row, or the sidebar. Removal
 * counts as a change too (save success, ghost sweep).
 */
export function subscribeStepOverrideKey(
  store: ReadonlyStepOverrideStore,
  key: string,
  onChange: () => void,
): () => void {
  return store.subscribe((state, previous) => {
    if (
      state.drafts.get(key) !== previous.drafts.get(key)
      || state.saveErrors.get(key) !== previous.saveErrors.get(key)
      // U2 — an acked change (the save-success move, or the sweep effect's
      // later release) is a real change to this key's effective value too.
      || state.acked.get(key) !== previous.acked.get(key)
    ) {
      onChange();
    }
  });
}

/**
 * U2 — the release predicate for the queue-driven sweep effect's acked pass:
 * "has a queue read now caught up with this acked write?" Mirrors
 * `itemReflectsPatch`'s role for the base-copy autosave rail's `ackedPatch`,
 * but needs no `blankToNull`-style normalization — verified against
 * `src/services/flow/step-drafts.ts`'s `upsertStepDraft`, which stores
 * `subject_draft`/`body_draft` VERBATIM (no blank-to-null rewrite, unlike
 * the item-level PATCH route's `blankToNull`).
 *
 * `isEmailStep` is threaded in by the caller (`EnrollQueueContext.tsx`,
 * which already imports `EMAIL_STEP_TYPES` from `StepOverrideEditor.tsx`)
 * rather than looked up here — this module deliberately has no import edge
 * back into a component file (see this file's own header).
 *
 * - A PUT-acked entry (`draft.values !== null`) is released once a
 *   `step_drafts` row exists for `draft.stepNumber` whose `body_draft`
 *   matches, AND — for an email step type only — whose `subject_draft`
 *   also matches (a non-email step's PUT omits the subject key entirely,
 *   `EnrollQueueContext.tsx`'s `saveStepOverrides`, so comparing it there
 *   would compare a value that was never written).
 * - A DELETE-acked entry (`draft.values === null`) is released once NO
 *   `step_drafts` row exists for that step number.
 */
export function stepDraftReflectsAckedDraft(
  stepDrafts: ReadonlyArray<Pick<StepDraft, 'step_number' | 'subject_draft' | 'body_draft'>>,
  draft: StepOverrideDraft,
  isEmailStep: boolean,
): boolean {
  const row = stepDrafts.find(d => d.step_number === draft.stepNumber);
  if (draft.values === null) return row === undefined;
  if (!row) return false;
  if (row.body_draft !== draft.values.body_draft) return false;
  if (isEmailStep && row.subject_draft !== draft.values.subject_draft) return false;
  return true;
}

/**
 * Read one subscribed value with zustand's React binding.
 *
 * `StepOverrideEditor` is deliberately portable (its own header, spec §7
 * rule 4): it receives `subscribeStep`/`localDraftFor`/`saveErrorFor` as
 * plain functions bound to one item — never the store, never the context —
 * so a second surface can mount it without adopting this module's state
 * shape. This hook is the adapter that lets that prop contract be read
 * through zustand's `useStore` instead of a hand-rolled
 * `useSyncExternalStore` call: it holds NO state and NO listener registry of
 * its own, it just presents the caller's already-memoized subscribe/read
 * pair in the read-only shape `useStore` consumes (for the enroll queue,
 * that pair reads a `createStepOverrideStore()` instance via
 * `subscribeStepOverrideKey`).
 *
 * `subscribe` and `read` must be referentially stable (memoize them, as
 * `StepOverrideRow` does) — a new identity resubscribes, exactly as it would
 * with a raw `useSyncExternalStore` pair. `read` must also return a
 * referentially stable value while nothing changed (React's "getSnapshot
 * should be cached" rule); the store's Map reads satisfy that.
 *
 * Deliberately narrow: this is the step-override editor's portable read
 * path, NOT a general-purpose `useSyncExternalStore` escape hatch. New state
 * gets a zustand store and `useStore(store, selector)` directly.
 */
export function useStepOverrideEntry<T>(subscribe: (onChange: () => void) => () => void, read: () => T): T {
  const view = useMemo(
    () => ({
      getState: read,
      getInitialState: read,
      // React's `useSyncExternalStore` calls listeners with no arguments and
      // ignores their parameters; the current value is passed for both so the
      // view satisfies zustand's `(state, prevState)` listener type honestly
      // rather than through a cast.
      subscribe: (listener: (state: T, previous: T) => void) =>
        subscribe(() => {
          const next = read();
          listener(next, next);
        }),
    }),
    [subscribe, read],
  );
  return useStore(view);
}
